<template>
  <div>
    <panel class="product" v-if="loading">
      <loader :loading="loading"/>
    </panel>

    <panel :title="`${product.sku} - ${product.title}`" :withFooter="false" :withBody="withBody" v-else>
      <div slot="header-right">
        <span class="badge" v-bind:class="product.statusSynchronization">{{ $t(`migration.statusSynchronization.${product.statusSynchronization}.label`) }}</span>
        <span class="badge" v-bind:class="product.status">{{ $t(`migration.status.${product.status}.label`) }}</span>
        <span class="badge" v-bind:class="productActive">{{ $t(`migration.statusProductMarketplace.${product.active}.label`) }}</span>
      </div>

      <div class="l-row">
        <div class="l-col-2@md vertical-center">
          <img :src="image" :alt="product.title" class="photo" v-if="image">
          <i class="icon icon-image2 no-photo" v-else></i>
        </div>

        <div class="l-col-10@md">
          <div class="l-row">
            <div class="l-col-12@md">
              <form-group label="Módulo" :columns="12">
                <select v-model="moduleSelect" class="form-select">
                  <option :value=null>{{ $t('choose.label') }}</option>
                  <option :value="`${module.type}.${module.identifier}`" v-for="module in modules">
                    {{ $t(`${module.type}.label`) }} {{ $t(`${module.identifier}.label`) }}
                  </option>
                </select>
              </form-group>

              <form-group label="Produto" :columns="12">
                <multiselect
                  v-model="productSource"
                  :multiple="false"
                  @search-change="search"
                  :options="products"
                  open-direction="bottom"
                  placeholder="Buscar por título/sku/ean/referencia"
                  label="name"
                  track-by="name"
                  :loading="isLoading"
                  :disabled="!moduleSelect"
                  :searchable="true"
                  :internal-search="false"
                  :clear-on-select="true"
                  :close-on-select="true"
                  :show-labels="false"
                  :options-limit="300"
                  :limit="3"
                  :max-height="600"
                  :show-no-results="false"
                  selectLabel="Clique"
                  selectedLabel=""/>
              </form-group>
            </div>
          </div>

          <div class="l-col-12@md" v-if="productSource">
            <button class="btn btn--info u-floatRight" @click="migrateProduct" v-bind:class="{ 'is-loading': isMigrationDoing }">{{ $t('migrate.label') }}</button>
          </div>
        </div>
      </div>
    </panel>
  </div>
</template>

<script>
  import { debounce } from 'lodash'
  import api from '@/api'
  import notifier from '@/notifier'
  import Panel from '@/components/template/Panel'
  import Loader from '@/components/template/Loader'
  import FormGroup from '@/components/template/FormGroup'
  import Multiselect from 'vue-multiselect'

  export default {
    data: () => ({
      loading: false,
      show: true,
      moduleSelect: null,
      productSource: null,
      products: [],
      details: null,
      withBody: false,
      isLoading: false,
      isMigrationDoing: false,
      productActive: 'pending'
    }),
    props: {
      product: {
        type: Object,
        required: true
      },
      module: {
        type: Object,
        required: true
      },
      modules: {
        type: Array,
        required: true
      }
    },
    created () {
      this.search = debounce(this.searchMethod, 3000)
    },
    mounted () {
      if (this.product.active) {
        this.productActive = 'done'
      }
      if (this.product.status !== 'done') {
        this.loading = true

        api.getProductsMigrationDetails(this.module.type, this.module.identifier, this.product.sku)
          .then(item => {
            this.details = item
            this.withBody = true

            if (this.details.source.length) {
              if (this.modules.length === 1) {
                this.details.source = this.modules[0]
              }
            }

            if (this.modules.length === 1) {
              this.moduleSelect = `${this.modules[0].type}.${this.modules[0].identifier}`
            }
          })
          .catch(() => (notifier.error('Falha ao buscar os produtos para migração.', 'Erro')))
          .finally(() => { this.loading = false })
      }
    },
    methods: {
      async searchMethod (search) {
        if (this.isLoading || !search) {
          return false
        }

        this.isLoading = true

        const items = await api.getProductsToMigration(
          this.details.source.type,
          this.details.source.identifier,
          search,
          { limit: 3 }
        )

        this.products = []
        for (let i in items) {
          let item = items[i]
          item.name = `${item.title} - ${item.brand}`

          this.products.push(item)
        }

        this.isLoading = false
      },
      async migrateProduct () {
        this.isMigrationDoing = true

        try {
          const product = Object.assign({}, this.details)

          await api.saveProductMigration(this.module.type, this.module.identifier, product)

          this.details.status = 'done'
          this.product.status = 'done'
          this.withBody = false

          notifier.success('Produto migrado com sucesso.', 'Sucesso')
        } catch (err) {
          notifier.error('Erro ao migrar produto.', 'Erro')
        }

        this.isMigrationDoing = false
      }
    },
    computed: {
      image () {
        if (this.loading || this.details === null) {
          return null
        }

        if (this.details && this.details.images && this.details.images.length) {
          return this.details.images[0]
        }

        return null
      }
    },
    watch: {
      moduleSelect (value) {
        this.productSource = null

        if (value === null) {
          this.details.source = null
          return false
        }

        const [type, identifier] = value.split('.')
        this.details.source = {
          type: type,
          identifier: identifier
        }
      },
      product (value) {
        if (value === null) {
          this.details.productId = null
          return false
        }

        this.details.productId = value.productId
      }
    },
    components: {
      Panel,
      Loader,
      FormGroup,
      Multiselect
    }
  }
</script>

<style lang="sass" scoped>
  .product
    min-height: 80px

  .photo
    max-width: 80%
    margin: 0 auto

  .no-photo
    font-size: 140px
    color: #a9a9a9
    margin: 0 auto

  .vertical-center
    display: flex
    align-items: center
    min-height: 81px

  .partial
    background: #FFA500
    color: #fff
</style>



// WEBPACK FOOTER //
// Card.vue?c2cc9222